import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import {
  Rocket, Search, FileText, Eye, CheckCircle2, XCircle,
  Clock, AlertCircle, Loader2, Package, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, X, Send, Sparkles, Link,
  PenTool, Grid3X3, BarChart3, Filter, Check, Ban,
  TrendingUp, Binoculars, FolderTree, Plus,
  Play, Pause, Settings2, ChevronDown, ChevronUp,
  Globe, Star, Weight, ShoppingCart, Zap, Edit3, Layers
} from 'lucide-react';
import { SITE_FLAGS } from '../shared';
import { useCan } from '../../../lib/rbac';

function ManualPublishForm({ prefillData }: { prefillData?: any }) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const { sellerId } = useUIStore();

  const [title, setTitle] = useState(prefillData?.title || '');
  const [description, setDescription] = useState(prefillData?.description || prefillData?.features?.join('\n') || '');
  const [price, setPrice] = useState(prefillData?.price || '');
  const [categoryId, setCategoryId] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [categorySearch, setCategorySearch] = useState(prefillData?.category_tree || '');
  const [categorySuggestions, setCategorySuggestions] = useState<any[]>([]);
  const [catLoading, setCatLoading] = useState(false);
  const [images, setImages] = useState<string[]>(prefillData?.images || ['']);
  const [brand, setBrand] = useState(prefillData?.brand || '');
  const [gtin, setGtin] = useState('');
  const [sites, setSites] = useState<string[]>(['MLA', 'MLM', 'MLC', 'MCO']);
  const [attrs, setAttrs] = useState<{ key: string; value: string }[]>([]);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [markupPreview, setMarkupPreview] = useState<any>(null);

  const asin = prefillData?.asin || '';

  // Update fields when prefill changes
  useEffect(() => {
    if (prefillData) {
      setTitle(prefillData.title || '');
      setDescription(prefillData.description || prefillData.features?.join('\n') || '');
      setPrice(prefillData.price || '');
      setBrand(prefillData.brand || '');
      setImages(prefillData.images?.length ? prefillData.images : ['']);
      setCategorySearch(prefillData.category_tree || '');
    }
  }, [prefillData]);

  // Markup preview when price changes
  useEffect(() => {
    if (!price || Number(price) <= 0) { setMarkupPreview(null); return; }
    const timeout = setTimeout(async () => {
      try {
        const res = await api.post<any>('/publisher/manual/markup-preview', { amazon_price: Number(price), seller_id: sellerId });
        if (res.success && res.data) setMarkupPreview(res.data);
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(timeout);
  }, [price, sellerId]);

  // Category search
  const handleCategorySearch = async () => {
    if (!categorySearch.trim() && !title.trim()) return;
    setCatLoading(true);
    try {
      const res = await api.post<any>('/publisher/manual/categorize', {
        title: title || categorySearch,
        category_path: categorySearch,
        seller_id: sellerId,
      });
      if (res.success && res.data) {
        const groups = res.data?.groups || res.data?.results || [];
        const suggestions: any[] = [];
        if (Array.isArray(groups)) {
          groups.forEach((g: any) => {
            if (g.matches) {
              g.matches.forEach((m: any) => suggestions.push(m));
            } else if (g.category_id) {
              suggestions.push(g);
            }
          });
        }
        setCategorySuggestions(suggestions);
      }
    } catch (err) {
      console.error('Categorize failed:', err);
    } finally {
      setCatLoading(false);
    }
  };

  // Validation score
  const calcScore = () => {
    let score = 100;
    if (!title) score -= 25;
    else if (title.length < 20) score -= 20;
    else if (title.length < 40) score -= 5;
    else if (title.length > 60) score -= 5;

    if (!description) score -= 25;
    else if (description.length < 200) score -= 20;
    else if (description.length < 800) score -= 10;

    if (!price || Number(price) <= 0) score -= 20;
    const validImages = images.filter(i => i.trim());
    if (validImages.length === 0) score -= 20;
    else if (validImages.length < 3) score -= 5;
    if (!categoryId) score -= 15;
    if (!brand) score -= 5;

    return Math.max(0, Math.min(100, score));
  };

  const score = calcScore();
  const isPublishable = score >= 60;

  const toggleSite = (site: string) => {
    setSites(prev => prev.includes(site) ? prev.filter(s => s !== site) : [...prev, site]);
  };

  const handleCreate = async () => {
    const validImages = images.filter(i => i.trim());
    if (!title || !price || !categoryId || validImages.length === 0) return;
    setCreating(true);
    setResult(null);
    try {
      const attrObj: Record<string, string> = {};
      attrs.forEach(a => { if (a.key.trim() && a.value.trim()) attrObj[a.key.trim()] = a.value.trim(); });

      const res = await api.post<any>('/publisher/manual/publish', {
        seller_id: sellerId,
        title,
        description,
        price: Number(price),
        category_id: categoryId,
        category_name: categoryName,
        images: validImages,
        attributes: attrObj,
        sites,
        asin: asin || undefined,
        brand,
        gtin: gtin || undefined,
      });
      if (res.success) {
        setResult(res.data);
      }
    } catch (err) {
      console.error('Create draft failed:', err);
    } finally {
      setCreating(false);
    }
  };

  if (result) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center space-y-3">
        <CheckCircle2 size={48} className="text-green-500 mx-auto" />
        <h3 className="text-lg font-bold text-green-700 dark:text-green-400">{t('pub.manual.created')}</h3>
        <p className="text-sm text-green-600 dark:text-green-500">Draft ID: {result.draft_id}</p>
        <button
          onClick={() => { setResult(null); setTitle(''); setDescription(''); setPrice(''); setImages(['']); }}
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:opacity-90"
        >
          {t('pub.manual.goToDrafts')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Form Fields */}
        <div className="lg:col-span-2 space-y-4">
          {/* Title */}
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
            <label className="text-sm font-medium text-[var(--color-text)] block mb-1">{t('pub.manual.title')}</label>
            <p className="text-xs text-[var(--color-text-tertiary)] mb-2">{t('pub.manual.titleHint')}</p>
            <textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-none"
            />
            <div className={`text-xs mt-1 text-right ${title.length > 60 ? 'text-orange-500' : 'text-[var(--color-text-tertiary)]'}`}>
              {title.length} {t('pub.manual.chars')}
            </div>
          </div>

          {/* Description */}
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
            <label className="text-sm font-medium text-[var(--color-text)] block mb-1">{t('pub.manual.description')}</label>
            <p className="text-xs text-[var(--color-text-tertiary)] mb-2">{t('pub.manual.descHint')}</p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-y"
            />
            <div className={`text-xs mt-1 text-right ${description.length < 800 ? 'text-orange-500' : 'text-green-500'}`}>
              {description.length} {t('pub.manual.chars')}
            </div>
          </div>

          {/* Price + Brand + GTIN */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
              <label className="text-sm font-medium text-[var(--color-text)] block mb-1">{t('pub.manual.price')}</label>
              <p className="text-xs text-[var(--color-text-tertiary)] mb-2">{t('pub.manual.priceHint')}</p>
              <input
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              />
              {canMoney && markupPreview && (
                <div className="text-xs text-[var(--color-text-tertiary)] mt-2">
                  {t('pub.manual.markupPreview')}: ~${markupPreview.estimated_ml_price || (Number(price) * (markupPreview.markup_factor || 2.5)).toFixed(2)}
                </div>
              )}
            </div>
            <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
              <label className="text-sm font-medium text-[var(--color-text)] block mb-1">{t('pub.manual.brand')}</label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] mt-6"
              />
            </div>
            <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
              <label className="text-sm font-medium text-[var(--color-text)] block mb-1">{t('pub.manual.gtin')}</label>
              <input
                type="text"
                value={gtin}
                onChange={(e) => setGtin(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] mt-6"
              />
            </div>
          </div>

          {/* Category */}
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
            <label className="text-sm font-medium text-[var(--color-text)] block mb-1">{t('pub.manual.category')}</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCategorySearch()}
                placeholder={t('pub.manual.searchCategory')}
                className="flex-1 px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              />
              <button
                onClick={handleCategorySearch}
                disabled={catLoading}
                className="flex items-center gap-1.5 px-3 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {catLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                {t('pub.feed.discover')}
              </button>
            </div>
            {categoryId && (
              <div className="flex items-center gap-2 text-xs text-green-600 mb-2">
                <CheckCircle2 size={12} />
                <span>{categoryName} ({categoryId})</span>
              </div>
            )}
            {categorySuggestions.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-0.5 border border-[var(--color-border)] rounded-lg p-1">
                {categorySuggestions.map((cat, i) => (
                  <button
                    key={cat.category_id || cat.id || i}
                    onClick={() => {
                      setCategoryId(cat.category_id || cat.id);
                      setCategoryName(cat.category_name || cat.path || cat.name || '');
                      setCategorySuggestions([]);
                    }}
                    className="w-full text-left px-3 py-1.5 rounded text-xs text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)] flex items-center gap-2 transition-colors"
                  >
                    <FolderTree size={12} className="text-[var(--color-text-tertiary)] shrink-0" />
                    <span>{cat.category_name || cat.path || cat.name || cat.category_id}</span>
                    {cat.confidence != null && (
                      <span className="ml-auto text-[var(--color-text-tertiary)]">{cat.confidence}%</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Images */}
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
            <label className="text-sm font-medium text-[var(--color-text)] block mb-2">{t('pub.manual.images')}</label>
            <div className="space-y-2">
              {images.map((img, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={img}
                    onChange={(e) => {
                      const next = [...images];
                      next[idx] = e.target.value;
                      setImages(next);
                    }}
                    placeholder="https://..."
                    className="flex-1 px-3 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs text-[var(--color-text)] font-mono"
                  />
                  {img.trim() && (
                    <img src={img} alt="" className="w-10 h-10 rounded object-cover border border-[var(--color-border)]" onError={(e) => (e.currentTarget.style.display = 'none')} />
                  )}
                  {images.length > 1 && (
                    <button
                      onClick={() => setImages(images.filter((_, i) => i !== idx))}
                      className="text-red-400 hover:text-red-600 text-xs"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setImages([...images, ''])}
              className="flex items-center gap-1 text-xs text-[var(--color-primary)] mt-2 hover:opacity-80"
            >
              <Plus size={12} />
              {t('pub.manual.addImage')}
            </button>
          </div>

          {/* Sites */}
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
            <label className="text-sm font-medium text-[var(--color-text)] block mb-2">{t('pub.manual.sites')}</label>
            <div className="flex gap-2 flex-wrap">
              {['MLA', 'MLM', 'MLC', 'MCO'].map(site => (
                <label key={site} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sites.includes(site)}
                    onChange={() => toggleSite(site)}
                    className="rounded border-[var(--color-border)]"
                  />
                  <span className="text-[var(--color-text)]">{SITE_FLAGS[site]} {site}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Attributes */}
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
            <label className="text-sm font-medium text-[var(--color-text)] block mb-2">{t('pub.manual.attributes')}</label>
            <div className="space-y-2">
              {attrs.map((attr, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={attr.key}
                    onChange={(e) => {
                      const next = [...attrs];
                      next[idx] = { ...next[idx], key: e.target.value };
                      setAttrs(next);
                    }}
                    placeholder={t('pub.manual.key')}
                    className="flex-1 px-3 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs text-[var(--color-text)]"
                  />
                  <input
                    type="text"
                    value={attr.value}
                    onChange={(e) => {
                      const next = [...attrs];
                      next[idx] = { ...next[idx], value: e.target.value };
                      setAttrs(next);
                    }}
                    placeholder={t('pub.manual.value')}
                    className="flex-1 px-3 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs text-[var(--color-text)]"
                  />
                  <button
                    onClick={() => setAttrs(attrs.filter((_, i) => i !== idx))}
                    className="text-red-400 hover:text-red-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setAttrs([...attrs, { key: '', value: '' }])}
              className="flex items-center gap-1 text-xs text-[var(--color-primary)] mt-2 hover:opacity-80"
            >
              <Plus size={12} />
              {t('pub.manual.addAttribute')}
            </button>
          </div>
        </div>

        {/* Right: Validation + Preview */}
        <div className="space-y-4">
          {/* Validation Panel */}
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4 sticky top-4">
            <h3 className="text-sm font-bold text-[var(--color-text)] mb-3 flex items-center gap-2">
              {t('pub.manual.validation')}
            </h3>
            <div className="flex items-center gap-3 mb-4">
              <div className={`text-3xl font-bold ${score >= 60 ? 'text-green-500' : 'text-red-500'}`}>
                {score}
              </div>
              <div>
                <div className={`text-sm font-medium ${score >= 60 ? 'text-green-600' : 'text-red-600'}`}>
                  {isPublishable ? t('pub.manual.publishable') : t('pub.manual.notPublishable')}
                </div>
                <div className="text-xs text-[var(--color-text-tertiary)]">{t('pub.manual.score')}: {score}/100</div>
              </div>
            </div>

            {/* Validation checklist */}
            <div className="space-y-1.5 text-xs">
              <ValidationItem
                ok={title.length >= 20}
                warn={title.length > 0 && title.length < 20}
                label={!title ? t('pub.manual.titleMissing') : title.length < 20 ? t('pub.manual.titleShort') : t('pub.manual.titleOk')}
              />
              <ValidationItem
                ok={description.length >= 800}
                warn={description.length > 0 && description.length < 800}
                label={!description ? t('pub.manual.descMissing') : description.length < 800 ? `${t('pub.manual.descShort')} (${description.length})` : t('pub.manual.descOk')}
              />
              <ValidationItem
                ok={!!price && Number(price) > 0}
                label={!price || Number(price) <= 0 ? t('pub.manual.priceMissing') : t('pub.manual.priceOk')}
              />
              <ValidationItem
                ok={!!categoryId}
                label={!categoryId ? t('pub.manual.catMissing') : t('pub.manual.catOk')}
              />
              <ValidationItem
                ok={images.filter(i => i.trim()).length > 0}
                warn={images.filter(i => i.trim()).length > 0 && images.filter(i => i.trim()).length < 3}
                label={images.filter(i => i.trim()).length === 0
                  ? t('pub.manual.imagesMissing')
                  : `${images.filter(i => i.trim()).length} ${t('pub.manual.imagesOk')}`}
              />
              <ValidationItem
                ok={!!brand}
                warn={!brand}
                label={brand ? t('pub.manual.brandOk') : t('pub.manual.brandMissing')}
              />
            </div>

            {/* Image previews */}
            {images.filter(i => i.trim()).length > 0 && (
              <div className="mt-4 flex gap-1 flex-wrap">
                {images.filter(i => i.trim()).slice(0, 6).map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt=""
                    className="w-12 h-12 rounded object-cover border border-[var(--color-border)]"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                ))}
              </div>
            )}

            {/* Create button */}
            <button
              onClick={handleCreate}
              disabled={creating || !isPublishable}
              className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-lg text-sm font-bold hover:opacity-90 disabled:opacity-50"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {creating ? t('pub.manual.creating') : t('pub.manual.createDraft')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ValidationItem({ ok, warn, label }: { ok: boolean; warn?: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {ok && !warn ? (
        <CheckCircle2 size={12} className="text-green-500 shrink-0" />
      ) : warn ? (
        <AlertCircle size={12} className="text-orange-500 shrink-0" />
      ) : (
        <XCircle size={12} className="text-red-400 shrink-0" />
      )}
      <span className={ok ? 'text-[var(--color-text)]' : warn ? 'text-orange-500' : 'text-red-400'}>
        {label}
      </span>
    </div>
  );
}

export { ManualPublishForm };
